import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { reviewService } from '../../../../components/Review/services/reviewService';
import { Review } from '../../../Data/Reviews';
import ReviewManagement from '../../../Reusables/ReviewManagement/ReviewManagement';
import { showSuccess, showError } from '../../../../Utilities/sweetAlert';

interface TrailReviewsProps {
  trailId: string | number;
}

const TrailReviews: React.FC<TrailReviewsProps> = ({ trailId }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrailReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reviewService.getTrailReviews(trailId);
      setReviews(response.data.reviews || []);
    } catch (err) {
      console.error('Error fetching trail reviews:', err);
      setError('Failed to load trail reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trailId) {
      fetchTrailReviews();
    }
  }, [trailId]);

  const handleReviewsChange = (updatedReviews: Review[]) => {
    setReviews(updatedReviews);
  };

  const handleRecalculateRating = async () => {
    try {
      setLoading(true);
      await reviewService.recalculateTrailRating(trailId);
      showSuccess('Rating Recalculated', 'Trail rating has been recalculated successfully');
      // Refetch reviews to show updated data
      await fetchTrailReviews();
    } catch (err) {
      console.error('Error recalculating rating:', err);
      showError('Recalculation Failed', 'Failed to recalculate trail rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Reviews for Trail #{trailId}</h5>
        <Button
          variant="outline-primary"
          onClick={handleRecalculateRating}
          disabled={loading}
          size="sm"
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Recalculate Trail Rating
        </Button>
      </div>
      <ReviewManagement
        reviews={reviews}
        loading={loading}
        error={error}
        title=""
        description="Manage all reviews (active, pending, deleted) for this specific trail."
        emptyMessage="No reviews found for this trail."
        removeOnAccept={false}
        removeOnReject={false}
        onReviewsChange={handleReviewsChange}
        onRefetch={fetchTrailReviews}
      />
    </>
  );
};

export default TrailReviews;